import React, {useState, useEffect, useCallback} from 'react';
import classNames from 'classnames';
import {
  EditableInlineText,
  useContentElementConfigurationUpdate,
  useContentElementEditorState,
  useAdditionalSeedData,
  useLocale
} from 'pageflow-scrolled/frontend';

import styles from './PricingCards.module.css';

export function PricingCards({configuration}) {
  const t = translations[useLocale()];
  const updateConfiguration = useContentElementConfigurationUpdate();
  const {isEditable} = useContentElementEditorState();

  const {
    planTableaux, selectPlans, hero, contractPeriod
  } = useAdditionalSeedData('pricing');

  const {
    availableCurrencies, taxRate, displayTaxRate, countryDisplayName
  } = useCountryInfo();

  const [selectedPeriod, setSelectedPeriod] = useState(contractPeriod || 'monthly');
  const [selectedCurrency, setSelectedCurrency] = useState(availableCurrencies[0]);

  const currency = availableCurrencies.includes(selectedCurrency) ?
                   selectedCurrency :
                   availableCurrencies[0];

  return (
    <div>
      <div className={styles.switches}>
        {availableCurrencies.length > 1 &&
         <Switch t={t}
                 options={availableCurrencies}
                 selectedOption={selectedCurrency}
                 onChange={setSelectedCurrency} />}

        {!contractPeriod &&
         <Switch t={t}
                 options={['monthly', 'yearly']}
                 selectedOption={selectedPeriod}
                 onChange={setSelectedPeriod} />}
      </div>

      <ul className={styles.cards}>
        {planTableaux.levels.map((level) => {
          return (
            <Card key={level.name}
                  variant={applyTax(level.variants[selectedPeriod][currency],
                                    taxRate)}
                  selectPlans={selectPlans}
                  heroLevelName={hero}
                  t={t}
                  description={configuration[`${level.name}Description`]}
                  tagline={configuration.tagline}
                  bullet0={configuration[`${level.name}Bullet0`]}
                  bullet1={configuration[`${level.name}Bullet1`]}
                  bullet2={configuration[`${level.name}Bullet2`]}
                  onChange={(property, value) => updateConfiguration(
                    property === 'Tagline' ?
                    {tagline: value} :
                    {[`${level.name}${property}`]: value}
                  )} />
          );
        })}
      </ul>
      <div className={styles.finePrint}>
        <EditableInlineText value={isEditable ?
                                   configuration.finePrint :
                                   replace(replace(configuration.finePrint,
                                                   '%{country}',
                                                   countryDisplayName || t.nonEU),
                                           '%{taxRate}',
                                           displayTaxRate)}
                            hyphens="manual"
                            onChange={finePrint => updateConfiguration({finePrint})}
                            placeholder="Text" />
      </div>
    </div>
  );
}

function replace(value, placeholder, text) {
  return [
    {
      children: [
        {text: value?.[0]?.children[0]?.text?.replace(placeholder, text)}
      ]
    }
  ];
}

function Switch({t, options, selectedOption, onChange}) {
  return (
    <div className={styles.switch}>
      {options.map(option =>
        <button key={option} className={classNames(styles.switchOption,
                                                   {[styles.selectedSwitchOption]:
                                                     option === selectedOption })}
                onClick={() => onChange(option)}>
          {t[option]}
        </button>
      )}
    </div>
  );
}

function Card({
  variant,
  selectPlans, heroLevelName,
  description, tagline,
  onChange, t,
  ...props
}) {
  const hero = heroLevelName ?
               variant.levelName === heroLevelName :
               variant.levelName === 'standard';

  const disabled = selectPlans &&
                   selectPlans !== '1' &&
                   !selectPlans.includes(variant.levelName);

  function onSelectButtonClick() {
    window.parent.postMessage({
      type: 'SELECT_PLAN',
      payload: {
        variant: variant
      }
    }, window.location.origin);
  }

  return (
    <li className={classNames(styles.card, {[styles.hero]: hero,
                                            [styles.disabled]: disabled})}>
      {hero && <div className={styles.tagline}>
        <EditableInlineText value={tagline}
                            hyphens="manual"
                            onChange={value => onChange('Tagline', value)}
                            placeholder="Tagline" />
      </div>}
      <h4 className={styles.level}>{variant.displayName}</h4>
      <div className={styles.price}>{variant.price.toFixed(2)}</div>
      <div className={styles.unit}>
        {variant.currencySign}/{t.month}
      </div>
      {selectPlans &&
       <button className={styles.selectPlan}
               disabled={disabled}
               aria-label={t.selectPlan.replace('%{plan}', variant.displayName)}
               onClick={onSelectButtonClick}>
         {t.select}
       </button>}
      <div className={styles.description}>
        <EditableInlineText value={description}
                            hyphens="manual"
                            onChange={value => onChange('Description', value)}
                            placeholder="Description" />
      </div>
      <ul className={styles.bullets}>
        {[...Array(3)].map((_, i) =>
          <li key={i}>
            <EditableInlineText value={props[`bullet${i}`]}
                                hyphens="manual"
                                onChange={value => onChange(`Bullet${i}`, value)}
                                placeholder={`Bullet ${i}`} />
          </li>
        )}
      </ul>
    </li>
  );
}

function useCountryInfo() {
  const {
    country,
    currency,
    countryDisplayNames,
    ipCountryDetectionUrl,
    euTaxRates
  } = useAdditionalSeedData('pricing');

  const [detectedCountry, setDetectedCountry] = useState(country || 'DE')

  useEffect(() => {
    if (!country) {
      fetch(ipCountryDetectionUrl)
        .then(response => response.json())
        .then(({ipCountry}) => {
          setDetectedCountry(ipCountry || 'DE');
        });
    }
  }, [country, ipCountryDetectionUrl]);

  const taxRate = euTaxRates[detectedCountry] || 1;

  return {
    countryDisplayName: countryDisplayNames[detectedCountry],
    availableCurrencies: currency ? [currency] :
                         euTaxRates[detectedCountry] ? ['eur'] :
                         ['usd', 'eur'],
    taxRate,
    displayTaxRate: `${Math.round((taxRate - 1) * 100)}%`,
  };
}

function applyTax(variant, taxRate) {
  return {
    ...variant,
    price: variant.price * taxRate
  };
}

const translations = {
  en: {
    nonEU: 'non-EU countries',
    yearly: 'Yearly',
    monthly: 'Monthly',
    month: 'Month',
    eur: 'Euro',
    usd: 'Dollar',
    selectPlan: 'Select %{plan}',
    select: 'Select'
  },
  de: {
    nonEU: 'Nicht-EU-Länder',
    yearly: 'Jährlich',
    monthly: 'Monatlich',
    month: 'Monat',
    eur: 'Euro',
    usd: 'Dollar',
    selectPlan: '%{plan} auswählen',
    select: 'Auswählen'
  },
};
